import Add10pxIcon from '@jetbrains/icons/add-10px.svg'
import Remove10pxIcon from '@jetbrains/icons/remove-10px.svg'
import Button from '@jetbrains/ring-ui/components/button/button'
import {Caption} from '@jetbrains/ring-ui/components/button-group/button-group'
import Dropdown from '@jetbrains/ring-ui/components/dropdown/dropdown'
import type {ListDataItem} from '@jetbrains/ring-ui/components/list/consts'
import PopupMenu from '@jetbrains/ring-ui/components/popup-menu/popup-menu'
import classNames from 'classnames'
import * as React from 'react'

import {Policy} from '../../../../../../../rest/branches'
import type {Branch} from '../../../../../../../services/rest'
import type {ProjectOrBuildTypeNode} from '../../../../../../../types'
import {parseBranch, stringifyBranch} from '../../../../../../../utils/branchNames'
import {notNull} from '../../../../../../../utils/guards'
import {BranchMultipleSelectContainer as BranchMultipleSelect} from '../../../../../../common/BranchSelect/BranchSelect.container'
import type {FilterBranchActivity, FilterBranchPattern} from '../../Rules.types'
import {FilterBranchStatus} from '../../Rules.types'
import {LoadingContext, DispatchContext} from '../CleanupForm.contexts'
import styles from '../CleanupForm.css'
import type {CleanupFormAction} from '../CleanupForm.types'

import type {
  BranchPatternAction,
  BranchPatternItem,
  BranchPatternState,
  Props,
} from './BranchesSection.types'

const defaultActivityItem = {
  name: FilterBranchStatus.ALL_BRANCHES,
  label: 'Active and Inactive',
} as const
const activityItems = [
  defaultActivityItem,
  {
    name: FilterBranchStatus.ACTIVE,
    label: 'Active',
  },
  {
    name: FilterBranchStatus.INACTIVE,
    label: 'Inactive',
  },
] as const
const MIN_TEXTAREA_ROWS = 3
const MAX_TEXTAREA_ROWS = 10
export const branchesActivityConverter = {
  toForm: (value: FilterBranchActivity | null | undefined): FilterBranchStatus | null | undefined =>
    value?.activity ?? null,
  toRule: (
    value: FilterBranchStatus | null | undefined,
  ): FilterBranchActivity | null | undefined =>
    value != null && value !== defaultActivityItem.name
      ? {
          activity: value,
        }
      : null,
}

const parseBranchPattern = (pattern: string): ReadonlyArray<BranchPatternItem> =>
  pattern.split('\n').map(line => {
    if (line.startsWith('+:')) {
      return {
        type: 'INCLUDE',
        branchName: line.slice(2),
      }
    }

    if (line.startsWith('-:')) {
      return {
        type: 'EXCLUDE',
        branchName: line.slice(2),
      }
    }

    return {
      type: 'UNKNOWN',
      text: line,
    }
  })

const stringifyBranchPattern = (items: ReadonlyArray<BranchPatternItem>): string =>
  items
    .map(item => {
      switch (item.type) {
        case 'INCLUDE':
          return `+:${item.branchName}`

        case 'EXCLUDE':
          return `-:${item.branchName}`

        default:
          return item.text
      }
    })
    .join('\n')

const defaultPattern = '+:*'
export const branchPatternConverter = {
  toForm(value: FilterBranchPattern | null | undefined): BranchPatternState {
    const pattern = value?.pattern ?? defaultPattern
    return {
      pattern,
      items: parseBranchPattern(pattern),
    }
  },

  toRule: ({pattern}: BranchPatternState): FilterBranchPattern | null | undefined =>
    pattern !== defaultPattern
      ? {
          pattern,
        }
      : null,
}
export function branchPatternReducer(
  state: BranchPatternState,
  action: CleanupFormAction,
): BranchPatternState {
  if (action.name !== 'branchPattern') {
    return state
  }

  switch (action.type) {
    case 'UPDATE_PATTERN': {
      const {pattern} = action
      return {
        pattern,
        items: parseBranchPattern(pattern),
      }
    }

    case 'ADD_BRANCH': {
      const {branchName} = action.item
      const items = state.items // remove same branch with different type
        .filter(item => item.type === 'UNKNOWN' || item.branchName !== branchName)
        .concat(action.item)
      return {
        pattern: stringifyBranchPattern(items),
        items,
      }
    }

    case 'REMOVE_BRANCH': {
      const {type, branchName} = action.item
      const items = state.items.filter(item => item.type !== type || item.branchName !== branchName)
      return {
        pattern: stringifyBranchPattern(items),
        items,
      }
    }

    default:
      return state
  }
}

const includeBranch = (
  actionType: 'ADD_BRANCH' | 'REMOVE_BRANCH',
  branch: Branch | null | undefined,
): BranchPatternAction => ({
  name: 'branchPattern',
  type: actionType,
  item: {
    type: 'INCLUDE',
    branchName: stringifyBranch(branch),
  },
})

const excludeBranch = (
  actionType: 'ADD_BRANCH' | 'REMOVE_BRANCH',
  branch: Branch | null | undefined,
): BranchPatternAction => ({
  name: 'branchPattern',
  type: actionType,
  item: {
    type: 'EXCLUDE',
    branchName: stringifyBranch(branch),
  },
})

function getIncludeExcludeBranches(
  items: ReadonlyArray<BranchPatternItem>,
  type: 'INCLUDE' | 'EXCLUDE',
): ReadonlyArray<Branch> {
  const result = []

  for (const item of items) {
    if (item.type === type) {
      result.push(parseBranch(item.branchName))
    }
  }

  return result.filter(notNull)
}

function BranchesSection({activeBranch, branchPattern, holderNode}: Props) {
  const disabled = React.useContext(LoadingContext)
  const dispatch = React.useContext(DispatchContext)
  const handleSelectActivityItem = React.useCallback(
    (option: ListDataItem<{name: FilterBranchStatus}>) =>
      dispatch({
        name: 'activeBranch',
        value: option.name,
      }),
    [dispatch],
  )
  const selectedActivityItem = React.useMemo(
    () => activityItems.find(item => item.name === activeBranch) ?? defaultActivityItem,
    [activeBranch],
  )
  const branchPatternValue = branchPattern.pattern
  const [branchPatternVisible, toggleTextareaVisible] = React.useReducer(
    visible => !visible,
    branchPatternValue !== '+:*',
  )
  const onBranchesChange = React.useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) =>
      dispatch({
        name: 'branchPattern',
        type: 'UPDATE_PATTERN',
        pattern: event.target.value,
      }),
    [dispatch],
  )
  const onIncludeBranchSelect = React.useCallback(
    (branch: Branch | null | undefined) => dispatch(includeBranch('ADD_BRANCH', branch)),
    [dispatch],
  )
  const onIncludeBranchDeselect = React.useCallback(
    (branch: Branch | null | undefined) => dispatch(includeBranch('REMOVE_BRANCH', branch)),
    [dispatch],
  )
  const onExcludeBranchSelect = React.useCallback(
    (branch: Branch | null | undefined) => dispatch(excludeBranch('ADD_BRANCH', branch)),
    [dispatch],
  )
  const onExcludeBranchDeselect = React.useCallback(
    (branch: Branch | null | undefined) => dispatch(excludeBranch('REMOVE_BRANCH', branch)),
    [dispatch],
  )
  const currentBranchesLineCount = branchPattern.items.length
  const currentTextareaRowsCount = React.useMemo(() => {
    let result = MIN_TEXTAREA_ROWS

    if (currentBranchesLineCount > MAX_TEXTAREA_ROWS) {
      result = MAX_TEXTAREA_ROWS
    } else if (currentBranchesLineCount > MIN_TEXTAREA_ROWS) {
      result = currentBranchesLineCount
    }

    return result
  }, [currentBranchesLineCount])
  const includedBranches = React.useMemo(
    () => getIncludeExcludeBranches(branchPattern.items, 'INCLUDE'),
    [branchPattern.items],
  )
  const excludedBranches = React.useMemo(
    () => getIncludeExcludeBranches(branchPattern.items, 'EXCLUDE'),
    [branchPattern.items],
  )
  const projectOrBuildTypeNode: ProjectOrBuildTypeNode | null =
    holderNode != null
      ? holderNode.nodeType === 'project'
        ? {
            nodeType: 'project',
            id: holderNode.id,
          }
        : {
            nodeType: 'bt',
            id: holderNode.id,
          }
      : null
  return (
    <div className={styles.row}>
      <Caption className={styles.caption}>{'Branches'}</Caption>
      <div className={styles.content}>
        <div className={styles.inputs}>
          <Dropdown
            anchor={
              <Button disabled={disabled} dropdown text className={styles.textButton}>
                {selectedActivityItem.label}
              </Button>
            }
          >
            <PopupMenu onSelect={handleSelectActivityItem} closeOnSelect data={activityItems} />
          </Dropdown>
          <Button
            onClick={toggleTextareaVisible}
            icon={branchPatternVisible ? Remove10pxIcon : Add10pxIcon}
            primary
          >
            {'Edit pattern'}
          </Button>
        </div>
        {branchPatternVisible ? (
          <>
            <div className={styles.inputs}>
              <textarea
                disabled={disabled}
                className={classNames(styles.textarea, styles.input)}
                placeholder={'Add pattern'}
                onChange={onBranchesChange}
                value={branchPatternValue}
                rows={currentTextareaRowsCount}
              />
            </div>
            <div className={styles.hint}>
              {
                'Newline-delimited set of rules in the form of +|-:logical branch name (with an optional * placeholder)'
              }
            </div>
            <div className={styles.inputs}>
              <BranchMultipleSelect
                label="Include"
                disabled={disabled}
                projectOrBuildTypeNode={projectOrBuildTypeNode}
                onSelect={onIncludeBranchSelect}
                onDeselect={onIncludeBranchDeselect}
                selected={includedBranches}
                policy={Policy.ALL_BRANCHES}
                directions={[
                  PopupMenu.PopupProps.Directions.BOTTOM_RIGHT,
                  PopupMenu.PopupProps.Directions.LEFT_TOP,
                ]}
              />
              <BranchMultipleSelect
                label="Exclude"
                disabled={disabled}
                projectOrBuildTypeNode={projectOrBuildTypeNode}
                onSelect={onExcludeBranchSelect}
                onDeselect={onExcludeBranchDeselect}
                className={styles.branchExclude}
                selected={excludedBranches}
                policy={Policy.ALL_BRANCHES}
                directions={[
                  PopupMenu.PopupProps.Directions.BOTTOM_RIGHT,
                  PopupMenu.PopupProps.Directions.TOP_RIGHT,
                ]}
              />
            </div>
          </>
        ) : null}
      </div>
    </div>
  )
}

export default React.memo<Props>(BranchesSection)
